<script setup>
import { ref, reactive, computed, onMounted } from 'vue'
import api from '@/services/api'
import { useAuthStore } from '@/stores/auth'
import { useMetadataListasStore } from '@/stores/metadataListas'
import FormularioCadastro from '@/components/base/formulario/FormularioCadastro.vue'
import AuditLogViewer from '@/components/base/auditoria/AuditLogViewer.vue'
import CampoBusca from '@/components/base/campos/CampoBusca.vue'
import CampoSelect from '@/components/base/campos/CampoSelect.vue'
import CampoInput from '@/components/base/campos/CampoInput.vue'
import { criarConfigBuscaTipoProduto } from '@/search-definitions/tipoProdutoSearch'
import { criarConfigBuscaProduto } from '@/search-definitions/produtoSearch'
import { formatarDataBR } from '@/utils/useDataBR'

const props = defineProps({
  itemParaEditar: {
    type: Object,
    default: null
  }
})

const emit = defineEmits(['itemAtualizado', 'cancelar', 'show-notification'])

const authStore = useAuthStore()
const metadataStore = useMetadataListasStore()

const isLoading = ref(false)
const salvando = ref(false)
const gerando = ref(false)
const aplicando = ref(false)

// Dados Básicos
const motivo = ref('')
const observacao = ref('')
const dataCorteReferencia = ref(null)

// Filtros
const idTipoProduto = ref(null)
const idProduto = ref(null)
const listasVinculadas = ref([])
const atributosSelecionados = reactive({})
const opcoesAtributos = reactive({})
const nomesListas = reactive({})

// Preview
const preview = ref([])
const paginacao = reactive({ CurrentPage: 1, PageSize: 50, TotalCount: 0, TotalPages: 1 })
const previewCarregado = ref(false)

// Rascunho
const rascunho = ref(null)

// Auditoria
const mostrarAuditoria = ref(false)

// Moedas e conversão
const moedaPadrao = ref('')
const moedaGerencial = ref('')
const taxaConversao = ref(null)
const taxaDataCotacao = ref(null)
const taxaFonte = ref(null)
const listaFontesCotacao = ref([])

// Listas de Produto (conversão de códigos para texto amigável)
const listaUnidades = ref([])

const configBuscaTipoProduto = computed(() => criarConfigBuscaTipoProduto(authStore.organizacaoAtiva))
const configBuscaProduto = computed(() => criarConfigBuscaProduto())

const temTipoSelecionado = computed(() => !!idTipoProduto.value)
const temRascunho = computed(() => !!rascunho.value)
const rascunhoEditavel = computed(() => rascunho.value?.lstStatus === 'CUSTO_ST1')

const entityInfo = computed(() => ({
  id: rascunho.value?.idAtualizacaoCusto ?? null,
  name: motivo.value || '—',
  nameLabel: 'Motivo'
}))

const taxaConversaoExibicao = computed(() => {
  if (taxaConversao.value === null || taxaConversao.value === undefined) return '—'
  return Number(taxaConversao.value).toLocaleString('pt-BR', { minimumFractionDigits: 4, maximumFractionDigits: 6 })
})

const taxaFonteTexto = computed(() => {
  if (!taxaFonte.value) return null
  const item = listaFontesCotacao.value.find(f =>
    String(f.idListaSistemaItem) === String(taxaFonte.value))
  return item?.textoLista || taxaFonte.value
})

// Sincroniza o header com os metadados de câmbio retornados pelo envelope salvo.
const sincronizarTaxaDoEnvelope = (envelope) => {
  if (!envelope) return
  if (envelope.taxaCambioGerencialPadrao !== undefined) taxaConversao.value = envelope.taxaCambioGerencialPadrao
  if (envelope.dataReferenciaCambio !== undefined) taxaDataCotacao.value = envelope.dataReferenciaCambio
  if (envelope.lstFonteCotacao !== undefined) taxaFonte.value = envelope.lstFonteCotacao
}

const labelNovoCustoPadrao = computed(() => `Novo Custo (${moedaPadrao.value || '—'})`)
const labelNovoCustoGerencial = computed(() => `Novo Custo (${moedaGerencial.value || '—'})`)
const labelCustoAtual = computed(() => `Custo Atual (${moedaPadrao.value || '—'})`)
const labelCustoReferencia = computed(() => `Custo Referência (${moedaPadrao.value || '—'})`)

const onTipoProdutoSelecionado = async (val) => {
  idTipoProduto.value = val?.idTipoProduto || null

  listasVinculadas.value = []
  Object.keys(atributosSelecionados).forEach(k => delete atributosSelecionados[k])
  Object.keys(opcoesAtributos).forEach(k => delete opcoesAtributos[k])
  Object.keys(nomesListas).forEach(k => delete nomesListas[k])
  limparPreview()

  if (!idTipoProduto.value) return

  try {
    const res = await api.get(`/TiposProduto/${idTipoProduto.value}`)
    listasVinculadas.value = res.data?.listasVinculadas || []

    for (const lista of listasVinculadas.value) {
      atributosSelecionados[lista.prefixoItemLista] = ''
      opcoesAtributos[lista.prefixoItemLista] = await metadataStore.getListaLookup(lista.prefixoItemLista, 'Cliente', true)
      nomesListas[lista.prefixoItemLista] = await obterNomeLista(lista.prefixoItemLista)
    }
  } catch (error) {
    emit('show-notification', { message: 'Não foi possível carregar as listas do tipo de produto.', type: 'error' })
  }
}

const obterNomeLista = async (prefixo) => {
  try {
    const res = await api.get(`/ListasCliente/por-prefixo/${prefixo}`)
    return res.data?.nomeLista || prefixo
  } catch (error) {
    return prefixo
  }
}

const onProdutoSelecionado = () => {
  limparPreview()
}

const limparPreview = () => {
  preview.value = []
  previewCarregado.value = false
  paginacao.CurrentPage = 1
  paginacao.TotalCount = 0
  paginacao.TotalPages = 1
}

const montarAtributos = () => {
  return Object.entries(atributosSelecionados)
    .filter(([, valor]) => valor !== '' && valor !== null && valor !== undefined)
    .map(([prefixoItemLista, lstValorAtributo]) => ({ prefixoItemLista, lstValorAtributo }))
}

const montarFiltro = () => ({
  idTipoProduto: idTipoProduto.value || null,
  idProduto: idProduto.value || null,
  atributos: montarAtributos()
})

const montarFiltrosJson = () => JSON.stringify(montarFiltro())

const buscarPreview = async (pageNumber = 1) => {
  isLoading.value = true
  try {
    const res = await api.post('/AtualizacoesCusto/preview', {
      ...montarFiltro(),
      pageNumber,
      pageSize: paginacao.PageSize
    })

    preview.value = res.data || []
    previewCarregado.value = true

    const header = res.headers?.['x-pagination']
    if (header) {
      const meta = JSON.parse(header)
      paginacao.CurrentPage = meta.CurrentPage
      paginacao.PageSize = meta.PageSize
      paginacao.TotalCount = meta.TotalCount
      paginacao.TotalPages = meta.TotalPages
    }

    if (paginacao.TotalCount === 0) {
      emit('show-notification', { message: 'Nenhum produto ativo e comercializável encontrado.', type: 'warning' })
    }
  } catch (error) {
    emit('show-notification', { message: extrairMensagem(error, 'Erro ao filtrar produtos.'), type: 'error' })
  } finally {
    isLoading.value = false
  }
}

const mudarPagina = (page) => {
  if (page >= 1 && page <= (paginacao.TotalPages || 1)) buscarPreview(page)
}

const salvarRascunho = async () => {
  if (motivo.value.trim() === '') {
    emit('show-notification', { message: 'O motivo da atualização é obrigatório.', type: 'warning' })
    return
  }

  salvando.value = true
  try {
    if (temRascunho.value) {
      // 1. Persiste os "Novo Custo" editados (mantém os valores locais).
      await api.put(`/AtualizacoesCusto/${rascunho.value.idAtualizacaoCusto}/itens`, {
        itens: rascunho.value.itens.map(i => ({
          idAtualizacaoCustoItem: i.idAtualizacaoCustoItem,
          custoUnitarioPadrao: i.custoUnitarioPadrao
        }))
      })

      // 2. Atualiza o cabeçalho (motivo/observação/data de corte).
      const res = await api.put(`/AtualizacoesCusto/${rascunho.value.idAtualizacaoCusto}`, {
        motivo: motivo.value.trim(),
        observacao: observacao.value || null,
        dataCorteReferencia: dataCorteReferencia.value || null
      })
      rascunho.value = res.data
      sincronizarTaxaDoEnvelope(res.data)

      emit('show-notification', { message: 'Rascunho atualizado com sucesso.', type: 'success' })
    } else {
      const res = await api.post('/AtualizacoesCusto/rascunho', {
        motivo: motivo.value.trim(),
        observacao: observacao.value || null,
        filtrosAplicadosJson: montarFiltrosJson(),
        dataCorteReferencia: dataCorteReferencia.value || null,
        filtro: montarFiltro()
      })

      rascunho.value = res.data
      sincronizarTaxaDoEnvelope(res.data)
      emit('show-notification', { message: 'Rascunho salvo. Agora você pode gerar as proposições.', type: 'success' })
    }
  } catch (error) {
    emit('show-notification', { message: extrairMensagem(error, 'Erro ao salvar o rascunho.'), type: 'error' })
  } finally {
    salvando.value = false
  }
}

const gerarProposicoes = async () => {
  if (!temRascunho.value) return

  gerando.value = true
  try {
    const res = await api.post(`/AtualizacoesCusto/${rascunho.value.idAtualizacaoCusto}/gerar-proposicoes`)
    rascunho.value = res.data
    sincronizarTaxaDoEnvelope(res.data)
    emit('show-notification', { message: 'Proposições de custo geradas.', type: 'success' })
  } catch (error) {
    emit('show-notification', { message: extrairMensagem(error, 'Erro ao gerar as proposições.'), type: 'error' })
  } finally {
    gerando.value = false
  }
}

const replicarReferencia = () => {
  if (!temRascunho.value) return
  for (const item of rascunho.value.itens) {
    if (item.custoReferenciaPadrao !== null && item.custoReferenciaPadrao !== undefined) {
      item.custoUnitarioPadrao = item.custoReferenciaPadrao
    }
  }
  emit('show-notification', { message: 'Custos de referência replicados para o Novo Custo.', type: 'info' })
}

const custoGerencialEfetivo = (item) => {
  if (item.custoUnitarioGerencial !== null && item.custoUnitarioGerencial !== undefined) {
    return item.custoUnitarioGerencial
  }
  if (item.custoUnitarioPadrao && taxaConversao.value) {
    return item.custoUnitarioPadrao / taxaConversao.value
  }
  return null
}

const carregarListasProduto = async () => {
  try {
    listaUnidades.value = await metadataStore.getListaPorCampo('Produto', 'LstUnidadeMedidaBase') || []
  } catch (error) {
    listaUnidades.value = []
  }
}

const textoUnidade = (codigo) => {
  if (!codigo) return '—'
  const item = listaUnidades.value.find(u =>
    String(u.idListaClienteItem) === String(codigo) || String(u.idListaSistemaItem) === String(codigo))
  return item ? item.textoLista : codigo
}

const aplicar = async () => {
  if (!temRascunho.value) return

  const semCusto = rascunho.value.itens.filter(i => !i.custoUnitarioPadrao || i.custoUnitarioPadrao <= 0)
  if (semCusto.length > 0) {
    emit('show-notification', { message: 'Todos os itens devem ter o Novo Custo (maior que zero).', type: 'warning' })
    return
  }

  aplicando.value = true
  try {
    // 1. Persiste os "Novo Custo" editados.
    await api.put(`/AtualizacoesCusto/${rascunho.value.idAtualizacaoCusto}/itens`, {
      itens: rascunho.value.itens.map(i => ({
        idAtualizacaoCustoItem: i.idAtualizacaoCustoItem,
        custoUnitarioPadrao: i.custoUnitarioPadrao
      }))
    })

    // 2. Aplica ao ledger.
    const res = await api.post(`/AtualizacoesCusto/${rascunho.value.idAtualizacaoCusto}/aplicar`)
    rascunho.value = res.data
    sincronizarTaxaDoEnvelope(res.data)
    emit('show-notification', { message: 'Atualização de custos aplicada com sucesso!', type: 'success' })
    emit('itemAtualizado')
  } catch (error) {
    emit('show-notification', { message: extrairMensagem(error, 'Erro ao aplicar a atualização.'), type: 'error' })
  } finally {
    aplicando.value = false
  }
}

const excluirRascunho = async () => {
  if (!temRascunho.value) return
  try {
    await api.delete(`/AtualizacoesCusto/${rascunho.value.idAtualizacaoCusto}`)
    rascunho.value = null
    emit('show-notification', { message: 'Rascunho excluído.', type: 'info' })
  } catch (error) {
    emit('show-notification', { message: extrairMensagem(error, 'Erro ao excluir o rascunho.'), type: 'error' })
  }
}

const carregarRascunhoAtivo = async () => {
  try {
    const res = await api.get('/AtualizacoesCusto/rascunho')
    if (res.status === 204 || !res.data) return
    aplicarEnvelope(res.data)
  } catch (error) {
    // Sem rascunho ativo — silencioso.
  }
}

const carregarEnvelope = async (id) => {
  try {
    const res = await api.get(`/AtualizacoesCusto/${id}`)
    if (!res.data) return
    aplicarEnvelope(res.data)
  } catch (error) {
    emit('show-notification', { message: extrairMensagem(error, 'Erro ao carregar a atualização de custos.'), type: 'error' })
  }
}

const aplicarEnvelope = (envelope) => {
  rascunho.value = envelope
  motivo.value = envelope.motivo || ''
  observacao.value = envelope.observacao || ''
  dataCorteReferencia.value = envelope.dataCorteReferencia || null
}

const carregarMoedas = async () => {
  try {
    const res = await api.get(`/Empresas/${authStore.empresaAtiva}`)
    const lstPadrao = res.data?.lstMoedaPadrao
    const lstGerencial = res.data?.lstMoedaGerencial

    const itens = await metadataStore.getListaLookup('MOE', 'Sistema')
    moedaPadrao.value = itens.find(i => String(i.idListaSistemaItem) === String(lstPadrao))?.textoLista || ''
    moedaGerencial.value = itens.find(i => String(i.idListaSistemaItem) === String(lstGerencial))?.textoLista || ''
  } catch (error) {
    // Silencioso — exibe "—" nos labels.
  }
}

const carregarListaFontes = async () => {
  try {
    listaFontesCotacao.value = await metadataStore.getListaLookup('MOE_FONTE_COTACAO', 'Sistema') || []
  } catch (error) {
    listaFontesCotacao.value = []
  }
}

const carregarTaxaConversao = async () => {
  try {
    const res = await api.get('/AtualizacoesCusto/taxa-conversao')
    taxaConversao.value = res.data?.taxaCambioGerencialPadrao ?? null
    taxaDataCotacao.value = res.data?.dataReferenciaCambio ?? null
    taxaFonte.value = res.data?.lstFonteCotacao ?? null
  } catch (error) {
    taxaConversao.value = null
    taxaDataCotacao.value = null
    taxaFonte.value = null
  }
}

const extrairMensagem = (error, fallback) => {
  const data = error.response?.data
  if (typeof data === 'string') return data
  if (data?.message) return data.message
  if (data?.title) return data.title
  if (data?.errors) return Object.values(data.errors).flat().join('; ')
  return fallback
}

const cancelar = () => {
  emit('cancelar')
}

const abrirAuditoria = () => {
  if (!temRascunho.value) return
  mostrarAuditoria.value = true
}

onMounted(async () => {
  await carregarMoedas()
  await carregarListaFontes()
  await carregarTaxaConversao()
  await carregarListasProduto()

  if (props.itemParaEditar?.idAtualizacaoCusto) {
    await carregarEnvelope(props.itemParaEditar.idAtualizacaoCusto)
  } else {
    await carregarRascunhoAtivo()
  }
})
</script>

<template>
  <FormularioCadastro
    module-name="Atualização de Custos"
    :is-loading="isLoading || salvando || gerando || aplicando"
    :is-editing="temRascunho"
    :entity-info="entityInfo"
    :ocultar-salvar="true"
    @cancelar="cancelar"
  >
    <template #acoes-header>
      <button
        v-if="temRascunho"
        type="button"
        class="btn-icon-minimalista me-3"
        @click="abrirAuditoria"
        title="Ver Histórico de Alterações"
      >
        <i class="bi bi-clock-history"></i>
      </button>
    </template>

    <template #info-extra>
      <div class="info-item">
        <strong>Taxa Câmbio {{ moedaGerencial || '—' }}/{{ moedaPadrao || '—' }}: </strong>
        <span>{{ taxaConversaoExibicao }}</span>
        <span v-if="taxaDataCotacao" class="text-body-primary small ms-2">
          (cotação de {{ formatarDataBR(taxaDataCotacao) }}<template v-if="taxaFonteTexto"> — {{ taxaFonteTexto }}</template>)
        </span>
      </div>
    </template>

    <!-- Aviso de Impacto -->
    <div class="alert alert-warning d-flex align-items-center mb-4 border-0 shadow-sm">
      <i class="bi bi-exclamation-triangle-fill fs-3 me-3 text-warning"></i>
      <div>
        <h6 class="alert-heading mb-1 fw-bold">Operação Imutável (Append-Only)</h6>
        <p class="mb-0 small">
          A aplicação encerra a vigência do custo anterior e cria um novo registro para todos os produtos do rascunho. O histórico é preservado.
        </p>
      </div>
    </div>

    <!-- Ação: Excluir Rascunho -->
    <div v-if="temRascunho" class="d-flex justify-content-start mb-3">
      <button type="button" class="btn btn-outline-danger" @click="excluirRascunho" :disabled="aplicando">
        <i class="bi bi-trash me-1"></i> Excluir Rascunho
      </button>
    </div>

    <!-- Card: Dados Básicos -->
    <div class="card mb-4">
      <div class="card-header">
        <h5 class="mb-0"><i class="bi bi-journal-text me-2 text-primary"></i> Dados Básicos</h5>
      </div>
      <div class="card-body">
        <div class="row g-3">
          <div class="col-md-6">
            <CampoInput
              id="motivo"
              label="Motivo"
              type="text"
              v-model="motivo"
              required
              :disabled="temRascunho"
              descricao-informativa="Ex: Atualização da família Alumínio por alta do LME."
            />
          </div>
          <div class="col-md-6">
            <CampoInput
              id="observacao"
              label="Observação"
              type="textarea"
              :rows="4"
              v-model="observacao"
              style="resize: vertical; min-height: 90px;"
              descricao-informativa="Texto livre com quebra de linha. Editável enquanto o rascunho não for aplicado."
            />
          </div>
          <div class="col-md-4">
            <CampoInput
              id="dataCorteReferencia"
              label="Data de Corte (Custo Referência)"
              type="date"
              v-model="dataCorteReferencia"
              :disabled="temRascunho"
              descricao-informativa="Opcional. Se vazio, usa a última atualização ou os últimos 3 meses."
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Card único: Filtros e Produtos -->
    <div class="card mb-4">
      <div class="card-header d-flex justify-content-between align-items-center">
        <h5 class="mb-0"><i class="bi bi-funnel me-2 text-primary"></i> Filtros e Produtos</h5>
        <div v-if="temRascunho" class="d-flex gap-2">
          <button
            type="button"
            class="btn btn-sm btn-outline-primary"
            @click="gerarProposicoes"
            :disabled="gerando || !rascunhoEditavel"
            title="Calcula o Custo de Referência (média ponderada das compras no período) para cada produto do rascunho. O período usa a Data de Corte informada; se vazia, usa desde a última atualização do produto (ou os últimos 3 meses, para produtos sem atualização anterior)."
          >
            <span v-if="gerando" class="spinner-border spinner-border-sm me-1"></span>
            <i v-else class="bi bi-calculator me-1"></i> Calcular Custos de Referência
          </button>
          <button
            type="button"
            class="btn btn-sm btn-outline-secondary"
            @click="replicarReferencia"
            :disabled="!rascunhoEditavel"
            title="Copia o Custo de Referência calculado para o campo Novo Custo de cada item, para você revisar e ajustar antes de aplicar."
          >
            <i class="bi bi-arrow-down-square me-1"></i> Replicar Referência
          </button>
          <button type="button" class="btn btn-sm btn-outline-primary" @click="salvarRascunho" :disabled="salvando || !rascunhoEditavel">
            <span v-if="salvando" class="spinner-border spinner-border-sm me-1"></span>
            <i v-else class="bi bi-save me-1"></i> Salvar Rascunho
          </button>
          <button type="button" class="btn btn-sm btn-success" @click="aplicar" :disabled="aplicando || !rascunhoEditavel">
            <span v-if="aplicando" class="spinner-border spinner-border-sm me-1"></span>
            <i v-else class="bi bi-check-lg me-1"></i> Aplicar Atualização
          </button>
        </div>
      </div>
      <div class="card-body">
        <!-- Filtros -->
        <div class="row g-3">
          <div class="col-md-4">
            <CampoBusca
              id="idTipoProduto"
              label="Tipo de Produto"
              v-model="idTipoProduto"
              :configuracao-busca="configBuscaTipoProduto"
              :disabled="temRascunho"
              @selecionado="onTipoProdutoSelecionado"
              @show-notification="p => emit('show-notification', p)"
            />
          </div>
          <div class="col-md-4">
            <CampoBusca
              id="idProduto"
              label="Produto (dispensa o Tipo)"
              v-model="idProduto"
              :configuracao-busca="configBuscaProduto"
              :disabled="temRascunho"
              @selecionado="onProdutoSelecionado"
              @show-notification="p => emit('show-notification', p)"
            />
          </div>
          <div class="col-md-4 d-flex align-items-end">
            <span class="text-body-secondary small">
              <i class="bi bi-info-circle me-1"></i>
              Sem filtros, todos os produtos comercializáveis serão incluídos.
            </span>
          </div>
        </div>

        <!-- Miniframe: Atributos do Tipo (sempre visível, bloqueado sem tipo) -->
        <div
          class="p-3 rounded-3 border mt-3"
          :class="temTipoSelecionado ? 'bg-light-subtle border-primary-subtle' : 'bg-body-tertiary border-secondary-subtle opacity-50'"
        >
          <div class="d-flex align-items-center mb-2">
            <i class="bi bi-tags me-2" :class="temTipoSelecionado ? 'text-primary' : 'text-secondary'"></i>
            <span class="small fw-semibold" :class="temTipoSelecionado ? 'text-primary' : 'text-secondary'">
              Atributos do Tipo de Produto
            </span>
            <span v-if="!temTipoSelecionado" class="badge bg-secondary ms-2" style="font-size: 0.65rem;">BLOQUEADO</span>
            <span v-else class="badge bg-primary ms-2" style="font-size: 0.65rem;">ATIVO</span>
          </div>
          <div v-if="temTipoSelecionado && listasVinculadas.length === 0" class="text-body-secondary small fst-italic">
            Este tipo não possui atributos. Você pode aplicar com o filtro de tipo ou sem filtro (todos os comercializáveis).
          </div>
          <div v-else class="row g-3">
            <div v-for="lista in listasVinculadas" :key="lista.prefixoItemLista" class="col-md-4">
              <CampoSelect
                :id="'attr_' + lista.prefixoItemLista"
                :label="nomesListas[lista.prefixoItemLista] || lista.prefixoItemLista"
                v-model="atributosSelecionados[lista.prefixoItemLista]"
                :options="opcoesAtributos[lista.prefixoItemLista] || []"
                option-value="idListaClienteItem"
                option-label="textoLista"
                placeholder="Todos"
                :disabled="!temTipoSelecionado || temRascunho"
              />
            </div>
          </div>
        </div>

        <!-- Ações de filtro -->
        <div v-if="!temRascunho" class="d-flex justify-content-end gap-2 mt-3">
          <button type="button" class="btn btn-outline-primary" @click="buscarPreview(1)" :disabled="isLoading">
            <span v-if="isLoading" class="spinner-border spinner-border-sm me-1"></span>
            <i v-else class="bi bi-search me-1"></i> Buscar Produtos
          </button>
          <button type="button" class="btn btn-primary" @click="salvarRascunho" :disabled="salvando">
            <span v-if="salvando" class="spinner-border spinner-border-sm me-1"></span>
            <i v-else class="bi bi-save me-1"></i> Salvar Rascunho
          </button>
        </div>

        <!-- Preview (antes do rascunho) -->
        <div v-if="!temRascunho && previewCarregado" class="table-responsive mt-3">
          <table class="table table-sm table-hover mb-0 align-middle">
            <thead class="table-light">
              <tr>
                <th>SKU</th>
                <th>Produto</th>
                <th>Un.</th>
                <th class="text-end">{{ labelCustoAtual }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="p in preview" :key="p.idProduto">
                <td class="text-muted small">{{ p.sku }}</td>
                <td>{{ p.nome }}</td>
                <td class="text-body-secondary small">{{ textoUnidade(p.lstUnidadeMedidaBase) }}</td>
                <td class="text-end">{{ p.custoAtualPadrao ?? '—' }}</td>
              </tr>
            </tbody>
          </table>
          <div v-if="paginacao.TotalPages > 1" class="d-flex justify-content-between align-items-center mt-3">
            <span class="text-body-secondary small">Página {{ paginacao.CurrentPage }} de {{ paginacao.TotalPages }}</span>
            <div class="btn-group">
              <button type="button" class="btn btn-sm btn-outline-secondary" :disabled="paginacao.CurrentPage <= 1 || isLoading" @click="mudarPagina(paginacao.CurrentPage - 1)">
                <i class="bi bi-chevron-left"></i> Anterior
              </button>
              <button type="button" class="btn btn-sm btn-outline-secondary" :disabled="paginacao.CurrentPage >= paginacao.TotalPages || isLoading" @click="mudarPagina(paginacao.CurrentPage + 1)">
                Próxima <i class="bi bi-chevron-right"></i>
              </button>
            </div>
          </div>
        </div>

        <!-- Itens do rascunho -->
        <div v-if="temRascunho" class="table-responsive mt-3">
          <table class="table table-sm table-hover mb-0 align-middle">
            <thead class="table-light">
              <tr>
                <th>SKU</th>
                <th>Produto</th>
                <th>Un.</th>
                <th class="text-end">{{ labelCustoAtual }}</th>
                <th class="text-end">{{ labelCustoReferencia }}</th>
                <th style="width: 160px;">{{ labelNovoCustoPadrao }}</th>
                <th class="text-end" style="width: 160px;">{{ labelNovoCustoGerencial }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in rascunho.itens" :key="item.idAtualizacaoCustoItem">
                <td class="text-muted small">{{ item.sku }}</td>
                <td>{{ item.nomeProduto }}</td>
                <td class="text-body-secondary small">{{ textoUnidade(item.lstUnidadeMedidaBase) }}</td>
                <td class="text-end">{{ item.custoAtualPadrao ?? '—' }}</td>
                <td class="text-end">{{ item.custoReferenciaPadrao ?? '—' }}</td>
                <td>
                  <CampoInput
                    :id="'novoCusto_' + item.idAtualizacaoCustoItem"
                    type="decimal"
                    :casas-decimais="6"
                    v-model.number="item.custoUnitarioPadrao"
                    :disabled="!rascunhoEditavel"
                    hide-label
                  />
                </td>
                <td class="text-end text-body-secondary">{{ custoGerencialEfetivo(item) ?? '—' }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Rodapé de ação -->
    <div v-if="temRascunho" class="d-flex justify-content-end gap-2">
      <button type="button" class="btn btn-outline-primary" @click="salvarRascunho" :disabled="salvando || !rascunhoEditavel">
        <span v-if="salvando" class="spinner-border spinner-border-sm me-1"></span>
        <i v-else class="bi bi-save me-1"></i> Salvar Rascunho
      </button>
      <button type="button" class="btn btn-success" @click="aplicar" :disabled="aplicando || !rascunhoEditavel">
        <span v-if="aplicando" class="spinner-border spinner-border-sm me-1"></span>
        <i v-else class="bi bi-check-lg me-1"></i> Aplicar Atualização
      </button>
    </div>
  </FormularioCadastro>

  <AuditLogViewer
    v-if="mostrarAuditoria && temRascunho"
    tabela="AtualizacaoCusto"
    :chave-primaria="rascunho.idAtualizacaoCusto"
    :titulo-registro="`Atualização #${rascunho.idAtualizacaoCusto}`"
    @fechar="mostrarAuditoria = false"
  />
</template>

<style scoped>
</style>
